// The BI app's icon set: inline SVG on a 24px grid, stroked in currentColor
// at 1.5px, same treatment as admin's `components/icons.tsx` (kept app-local
// for the same reason — @bi/shared exposes no icon primitive). Only
// the handful this app actually needs; grow it as pages are added rather
// than porting admin's whole set speculatively.
import type { ReactNode, SVGProps } from "react";

export interface IconProps extends SVGProps<SVGSVGElement> {
  /** Rendered square size in px (viewBox stays 24). */
  size?: number;
}

function Icon({ size = 20, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Empty states — an inbox. */
export function InboxIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M22 12.5h-5.5l-2 3h-5l-2-3H2" />
      <path d="M5.5 5.5h13L22 12.5V17a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4.5z" />
    </Icon>
  );
}

/** Error states — an alert circle. */
export function AlertIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4.5" />
      <path d="M12 16h.01" />
    </Icon>
  );
}

/** A bar chart — the dashboard catalog's home/back link. */
export function ChartIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 20V10" />
      <path d="M12 20V4" />
      <path d="M20 20v-7" />
      <path d="M3 20h18" />
    </Icon>
  );
}

export function MoonIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z" />
    </Icon>
  );
}

export function SunIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Icon>
  );
}

/** A clock: a chart card's "Refreshing" badge. */
export function ClockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Icon>
  );
}

/** A bolt. No component renders it. */
export function BoltIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M13 2 4 14h6l-1 8 9-12h-6z" />
    </Icon>
  );
}

/** An info circle — a chart's metric-spec affordance (`ChartSpec.description`,
 *  frequently the ONLY place a chart's numerator/denominator/window is
 *  written down; every chart that carries one must surface it somewhere
 *  reachable). */
export function InfoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </Icon>
  );
}

/** Four corner brackets — the fullscreen-view trigger on a chart card. */
export function ExpandIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 4H4v5" />
      <path d="M15 4h5v5" />
      <path d="M9 20H4v-5" />
      <path d="M15 20h5v-5" />
    </Icon>
  );
}

/** A tray with an arrow into it — the CSV download trigger on a chart card,
 *  next to the fullscreen one. */
export function DownloadIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3v12" />
      <path d="M7 10l5 5 5-5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </Icon>
  );
}

/** An X — dismisses a modal. */
export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 6l12 12" />
      <path d="M18 6l-12 12" />
    </Icon>
  );
}

/** Ask Mira BI feedback: a good answer. */
export function ThumbsUpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7 11v9H4a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h3z" />
      <path d="M7 11l4.5-7.5A2 2 0 0 1 13.5 4a2 2 0 0 1 2 2.4L14.5 11H19a2 2 0 0 1 2 2.3l-1.1 6A2 2 0 0 1 17.9 21H10a3 3 0 0 1-3-3v-7z" />
    </Icon>
  );
}

/** Ask Mira BI feedback: a bad answer. Same glyph as `ThumbsUpIcon`, flipped. */
export function ThumbsDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M17 13V4h3a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-3z" />
      <path d="M17 13l-4.5 7.5a2 2 0 0 1-2 .1 2 2 0 0 1-2-2.4L9.5 13H5a2 2 0 0 1-2-2.3l1.1-6A2 2 0 0 1 6.1 3H14a3 3 0 0 1 3 3v7z" />
    </Icon>
  );
}
